import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";

// ✅ Keep your SEO hooks path as in your project
import { useSEO, useJsonLD } from "./SEO";

// ===== Theme tokens (matched to your Fees.jsx) =====
const ACCENT = "#F0B90B"; // gold
const PAGE_BG = "#0B0E11"; // page background
const BG = "#263238"; // dark surface for content
const SURFACE = "rgba(38,50,56,0.85)"; // glassy card (sidebar/hero shells)
const BORDER = "rgba(255,255,255,0.08)"; // subtle borders
const TEXT = "#EAEFF2"; // primary text
const MUTED = "#B0BEC5"; // secondary text

function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: { xs: 88, md: 110 }, mb: 5 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          letterSpacing: 0.3,
          color: ACCENT,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          color: MUTED,
          "& p": { mb: 1.5, lineHeight: 1.75, color: MUTED },
          "& ul": { pl: 3, mb: 1.5, color: MUTED },
          "& li": { mb: 0.75 },
          "& a": { color: ACCENT, fontWeight: 700 },
        }}
      >
        {children}
      </Box>
      <Divider sx={{ mt: 3, borderColor: BORDER }} />
    </Box>
  );
}

export default function PrivacyPolicy({
  brandName = "1CryptoX",
  contactEmail = "support@1cryptox.com",
  canonical = "https://www.1cryptox.com/privacy",
  lastUpdatedISO = new Date().toISOString(),
}) {
  const lastUpdated = useMemo(() => {
    try {
      const d = new Date(lastUpdatedISO);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch {
      return lastUpdatedISO;
    }
  }, [lastUpdatedISO]);

  // ---------- SEO ----------
  const title = `Privacy Policy • ${brandName}`;
  const description =
    `${brandName} Privacy Policy: Learn how we collect, use, protect, and disclose personal information ` +
    `across our websites, apps, and digital-asset services.`;
  const keywords = [
    "Privacy Policy",
    "data protection",
    "KYC",
    "AML",
    "crypto",
    `${brandName}`,
  ];

  useSEO({
    title,
    description,
    keywords,
    canonical,
    robots: "index,follow",
    og: {
      type: "website",
      title,
      description,
      url: canonical,
      image: "https://www.1cryptox.com/og/og-privacy.jpg",
      siteName: brandName,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image: "https://www.1cryptox.com/og/og-privacy.jpg",
    },
  });

  // ---------- JSON-LD ----------
  useJsonLD("org", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brandName,
    url: canonical.replace("/privacy", ""),
    logo: "https://www.1cryptox.com/logo.png",
  });

  useJsonLD("webpage", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical,
    dateModified: lastUpdatedISO,
  });

  useJsonLD("breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: canonical.replace("/privacy", "/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Privacy Policy",
        item: canonical,
      },
    ],
  });

  const items = [
    { id: "overview", label: "1. Overview" },
    { id: "collection", label: "2. Information We Collect" },
    { id: "use", label: "3. How We Use Information" },
    { id: "cookies", label: "4. Cookies & Tracking" },
    { id: "sharing", label: "5. Sharing & Disclosures" },
    { id: "intl", label: "6. International Transfers" },
    { id: "retention", label: "7. Data Retention" },
    { id: "security", label: "8. Security" },
    { id: "rights", label: "9. Your Rights" },
    { id: "children", label: "10. Children’s Privacy" },
    { id: "changes", label: "11. Changes to this Policy" },
    { id: "contact", label: "12. Contact Us" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // ✅ same golden radial gradient used in your Fees page
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: BORDER,
        }}
      >
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              border: `1px solid rgba(240,185,11,0.35)`,
              bgcolor: "rgba(240,185,11,0.08)",
              color: ACCENT,
              fontSize: 12,
              letterSpacing: 0.6,
              fontWeight: 700,
            }}
          >
            PRIVACY & COMPLIANCE
          </Box>

          <Typography
            variant="h3"
            sx={{ mt: 2, fontWeight: 800, letterSpacing: 0.2, color: TEXT }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 1.5, maxWidth: 820, color: MUTED }}
          >
            This Privacy Policy explains how {brandName} (“we”, “us”, “our”)
            collects, uses, discloses, and protects personal information when
            you access our websites, mobile apps, and related services
            (collectively, the “Services”).
          </Typography>

          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: MUTED }}
          >
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={4} lg={3} order={{ xs: 2, md: 1 }}>
            <Paper
              elevation={0}
              sx={{
                position: { md: "sticky" },
                top: { md: 96 },
                p: 2,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: MUTED, fontWeight: 700 }}
              >
                On this page
              </Typography>
              <List dense disablePadding>
                {items.map((it) => (
                  <ListItem key={it.id} disableGutters sx={{ mb: 0.25 }}>
                    <ListItemButton
                      component="a"
                      href={`#${it.id}`}
                      sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.75,
                        color: TEXT,
                        "&:hover": {
                          bgcolor: "rgba(240,185,11,0.12)",
                        },
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ fontSize: 13 }}
                        primary={it.label}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={8} lg={9} order={{ xs: 1, md: 2 }}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              <Section id="overview" title="1. Overview">
                <p>
                  {brandName} provides digital-asset and fintech Services. By
                  accessing or using the Services, you acknowledge that you have
                  read and understood this Policy and agree to its terms, in
                  addition to our Terms of Use and applicable regional notices.
                </p>
              </Section>

              <Section id="collection" title="2. Information We Collect">
                <ul>
                  <li>
                    <strong>Account & Identity:</strong> name, email, country or
                    region, phone, and—where required—KYC verification data.
                  </li>
                  <li>
                    <strong>Financial & Transactional:</strong> wallet
                    identifiers, balances, deposits/withdrawals, transfers,
                    trade history, payment confirmations.
                  </li>
                  <li>
                    <strong>Technical:</strong> device and app identifiers, IP
                    address, OS/browser details, crash logs, language, timezone.
                  </li>
                  <li>
                    <strong>Usage & Analytics:</strong> pages viewed, clicks,
                    feature adoption, referral codes, campaign attribution.
                  </li>
                  <li>
                    <strong>Support:</strong> messages, attachments, session
                    metadata, quality reviews.
                  </li>
                </ul>
                <p>
                  Legal bases (as applicable): performance of contract,
                  legitimate interests (including security and product
                  improvement), consent, and compliance with legal obligations
                  (e.g., KYC/AML).
                </p>
              </Section>

              <Section id="use" title="3. How We Use Information">
                <ul>
                  <li>Provide, operate, and improve core account features.</li>
                  <li>Process transactions and maintain accurate records.</li>
                  <li>
                    Secure accounts; detect, prevent, and investigate fraud.
                  </li>
                  <li>
                    Communicate service updates, security alerts, and marketing
                    (where permitted by law).
                  </li>
                  <li>Analyze performance, debug issues, and enhance UX.</li>
                  <li>Comply with KYC/AML and other legal requirements.</li>
                </ul>
              </Section>

              <Section id="cookies" title="4. Cookies & Tracking">
                <p>
                  We use cookies, local storage, and similar technologies to
                  remember preferences, authenticate sessions, analyze usage,
                  and measure campaigns. You can control cookies via browser or
                  in-app settings; disabling some may impact functionality.
                </p>
              </Section>

              <Section id="sharing" title="5. Sharing & Disclosures">
                <p>We may share information with:</p>
                <ul>
                  <li>
                    <strong>Service providers</strong> (cloud, analytics,
                    security, KYC vendors) under contractual safeguards.
                  </li>
                  <li>
                    <strong>Affiliates/partners</strong> supporting joint
                    features or operations.
                  </li>
                  <li>
                    <strong>Legal/Compliance</strong> recipients when required
                    to protect users, rights, and lawful interests.
                  </li>
                  <li>
                    <strong>Business transfers</strong> related to mergers,
                    acquisitions, or asset sales.
                  </li>
                </ul>
                <p>
                  We do not sell personal information, nor allow third parties
                  to use your data for their own purposes without your consent.
                </p>
              </Section>

              <Section id="intl" title="6. International Transfers">
                <p>
                  Your information may be processed outside your country. Where
                  required, we implement appropriate safeguards (e.g., standard
                  contractual clauses) to protect cross-border transfers.
                </p>
              </Section>

              <Section id="retention" title="7. Data Retention">
                <p>
                  We retain personal information as long as necessary to provide
                  the Services, comply with legal/regulatory obligations,
                  resolve disputes, and enforce agreements. Retention periods
                  vary by record type and jurisdiction.
                </p>
              </Section>

              <Section id="security" title="8. Security">
                <p>
                  We apply administrative, technical, and physical safeguards to
                  help protect information against unauthorized access, loss, or
                  misuse. No method is 100% secure; we continuously improve our
                  controls in line with industry practices.
                </p>
              </Section>

              <Section id="rights" title="9. Your Rights">
                <p>
                  Depending on your location, you may request access,
                  correction, deletion, restriction, objection, or portability
                  of your data, and withdraw consent where applicable.
                </p>
                <p>
                  To exercise your rights, contact us at{" "}
                  <Link
                    href={`mailto:${contactEmail}`}
                    underline="always"
                    sx={{ color: ACCENT, fontWeight: 700 }}
                  >
                    {contactEmail}
                  </Link>
                  . We may verify your identity before acting on a request.
                </p>
              </Section>

              <Section id="children" title="10. Children’s Privacy">
                <p>
                  The Services are not intended for individuals under 18 (or the
                  age of majority in their jurisdiction). We do not knowingly
                  collect data from minors. If you believe a minor provided
                  data, please contact us promptly.
                </p>
              </Section>

              <Section id="changes" title="11. Changes to this Policy">
                <p>
                  We may update this Policy periodically. When we do, we revise
                  the “Last updated” date and, where appropriate, provide
                  additional notice. Continued use of the Services signifies
                  acceptance of the updated Policy.
                </p>
              </Section>

              <Section id="contact" title="12. Contact Us">
                <p>
                  Questions or concerns? Reach us at{" "}
                  <Link
                    href={`mailto:${contactEmail}`}
                    underline="always"
                    sx={{ color: ACCENT, fontWeight: 700 }}
                  >
                    {contactEmail}
                  </Link>
                  .
                </p>
                <Typography variant="caption" sx={{ color: MUTED }}>
                  This page is provided for transparency and does not replace
                  our Terms of Use or regional notices. In case of conflict, the
                  Terms and applicable laws prevail.
                </Typography>
              </Section>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
